import React, { forwardRef } from 'react';
import merge from 'lodash/merge';

import { cn } from '@lyearn/style';
import { Box, Stack } from '@lyearn/ui';
import { mainContentStyles } from '@/components/EditorBuilder/styles';
import { Image } from '@/components/Image';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';

import {
  bannerOverlayStyles,
  containerStyles,
  imageStyles,
} from '../../EditorBuilder/BannerWithTitle/styles';
import CompletedProgressLabel from '../CompletedProgressLabel';
import LessonTitle from '../LessonTitle';
import Badges from './badges';
import Points from './points';
import { titleWithBannerStyles } from './styles';
import { BannerWithTitleProps } from './types';
const defaultArticleCover = 'https://cdn.lyearn.com/assets/images/default_article_banner_1.png';
const DEFAULT_CLASSES: BannerWithTitleProps['classes'] = {
  titleContainer: 'pb-80',
  title: mainContentStyles,
};

const BannerWithTitle = forwardRef<HTMLDivElement, BannerWithTitleProps>((props, forwardedRef) => {
  const {
    src,
    yOffsetPercent,
    title,
    className,
    classes: classesProp,
    isCurrentLessonOptional,
    progress,
    overrides,
    languageSelector,
    points,
    badges,
    alwaysShowBanner,
    showPointsAndBadges,
  } = props;
  const imageCoverUrl = !!src ? src : alwaysShowBanner ? defaultArticleCover : null;
  const { [FeatureFlags.EnableGamification]: isGamificationEnabled } = useFeatureFlags();
  if (!imageCoverUrl) {
    return null;
  }

  const classes = merge(DEFAULT_CLASSES, classesProp);

  return (
    <Box
      ref={forwardedRef}
      className={cn(
        'relative mb-16 flex w-full flex-shrink-0 overflow-hidden',
        containerStyles,
        className,
      )}>
      <Image
        alt="lesson banner"
        className={cn('flex-1', imageStyles(yOffsetPercent))}
        data-id="banner-image"
        layout="fill"
        src={imageCoverUrl}
      />
      {imageCoverUrl != defaultArticleCover ? (
        <Box className={cn('absolute h-full w-full', bannerOverlayStyles)} />
      ) : null}
      <Stack
        alignItems="end"
        className={cn('absolute bottom-0 h-full w-full', classes.titleContainer)}
        justifyContent="center">
        <Stack className={classes.title} direction="col">
          <CompletedProgressLabel progress={progress} />
          <Stack direction="row" gapX={16}>
            <LessonTitle
              className={titleWithBannerStyles}
              color={overrides?.title?.color}
              isCurrentLessonOptional={isCurrentLessonOptional}
              title={title}
              titleClassName={classes.lessonTitle}
              variant={overrides?.title?.variant}
            />
            {languageSelector}
          </Stack>
          {showPointsAndBadges ? (
            <Stack alignItems="center" gapX={8}>
              {isGamificationEnabled ? <Points points={points} /> : null}
              {isGamificationEnabled ? <Badges badges={badges} /> : null}
            </Stack>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
});

export default React.memo(BannerWithTitle);
